<template>
  <div class="amazon-media-input">
    <div class="form-group row" :class="{ 'has-feedback' : isDirty }">
      <label :for="options.name" :class="options.fullWidth ? 'col-sm-12' : 'col-sm-3 col-form-label'">
        {{ options.label }}
        <span v-if="options.required" class="required">*</span>
      </label>
      <div :class="options.fullWidth ? 'col-sm-12' : 'col-sm-9'">
        <div class="upload-btn-wrapper" ref="dropzone">
          <button class="btn btn-info upload-button" type="button">Choose file...</button>
          <input type="hidden" :name="options.name" :id="options.name + '-url'" :value="value" />
        </div>
        <div v-if="hasError" class="alert alert-danger my-0">
          {{ hasError }}
        </div>
        <br/>
        <div v-if="value.key" class="card mb-3">
          <div class="card-header">
            <i class="fa fa-image"></i> {{ value.fileName }}
            <i
              class="fa interactive-cursor fa-lg mt-1 float-right"
              :class="open ? 'fa-chevron-up' : 'fa-chevron-down'" 
              @click="open = !open">
            </i>
          </div>
          <div class="card-body text-center" v-if="open">
            <img v-if="value.fileType != 'pdf'" target="_blank" class="img-fluid" :src="'https://roscon.s3.amazonaws.com/' + value.key"/>
            <span v-else>PDF</span>
          </div>
          <div class="card-footer text-right">
            <a target="_blank" :href="'https://roscon.s3.amazonaws.com/' + value.key" class="btn btn-primary">
              <span class="fa fa-search"></span> Preview File
            </a>
            <button @click.prevent="validate({})" class="btn btn-danger">
              <span class="fa fa-trash"></span> Remove File
            </button>
          </div>
        </div>
        <div class="progress" style="height: 20px;" v-show="isUploading">
          <div class="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" :style="{ width: progress + '%' }">
            {{ progress }}%
          </div>
        </div>
        <span :class="validationClass" v-if="validationMessage">
          {{ validationMessage }}
        </span>
      </div>
    </div>
  </div>
</template>


<script>
import {
  validationClass,
  validationMessage
} from '@/util/helpers'
import XLSX from 'xlsx'

export default {
  name: 'AmazonMediaInput',
  props: {
    value: {
      type: Object
    },
    options: {
      type: Object,
      default () {
        return {
          name: '',
          label: '',
          required: false
        }
      }
    }
  },
  data () {
    return {
      progress: 0,
      isUploading: false,
      hasError: false,
      isDirty: false,
      isValid: false,
      validType: true,
      open: true,
      jsonFormat: '',
      fileTypes: {
        excel: /^(application\/(vnd.openxmlformats-officedocument.spreadsheetml.sheet))$/,
        image: /^(image\/(gif|jpe?g|png))$/,
        pdf: /^(application\/(pdf|x-pdf|acrobat))|(text\/(pdf|x-pdf))|(applications\/vnd\.pdf)$/
      },
      validFileType: /^(image\/(gif|jpe?g|png))|(application\/(pdf|x-pdf|acrobat))|(text\/(pdf|x-pdf))|(applications\/vnd\.pdf)|(application\/(vnd.openxmlformats-officedocument.spreadsheetml.sheet))$/,
      invalidFileTypeError: 'Not an accepted file type. Accepted file types: JPG, PNG, GIF, XLSX and PDF'
    }
  },
  computed: {
    height () {
      return this.options.height || 'auto'
    },
    width () {
      return this.options.width || 'auto'
    },
    url () {
      if (this.options.straightToData) {
        return `/api/admin/media-uploader?height=${this.height}&width=${this.width}&data=true`
      } else {
        return `/api/admin/media-uploader?height=${this.height}&width=${this.width}`
      }
    },
    validationClass,
    validationMessage,
    invalidMessage () {
      if (!this.validType) {
        let validTypes = ''
        for (let i in this.options.fileTypes) {
          validTypes += this.options.fileTypes[i].toUpperCase()
          if (parseInt(i) !== this.options.fileTypes.length - 1) {
            validTypes += ' or '
          }
        }
        return 'You must upload a ' + validTypes + '. You have uploaded a ' + this.value.fileType.toUpperCase() + '.'
      } else if (this.options.required) {
        return this.options.label + ' is required'
      }
    }
  },
  mounted () {
    if (this.options.validFileType === 'image') {
      this.validFileType = this.fileTypes.image
      this.invalidFileTypeError = 'You must upload an image file. Accepted file types: JPG, PNG and GIF'
    } else if (this.options.validFileType === 'pdf') {
      this.validFileType = this.fileTypes.pdf
      this.invalidFileTypeError = 'You must upload a PDF file.'
    } else if (this.options.validFileType === 'excel') {
      this.validFileType = this.fileTypes.excel
      this.invalidFileTypeError = 'You must upload an excel file. Accepted file type: XLSX.'
    }
    if (this.options.open === false) this.open = false
    window.fd.logging = false
    let zone = new window.FileDrop(this.$refs.dropzone, {
      iframe: {
        url: this.url,
        fileParam: 'media',
        callbackParam: 'iframeCallback'
      }
    })
    let $vm = this
    zone.event('send', function (files) {
      $vm.isUploading = true
      files.each(function (file) {
        let jsonArray
        if ($vm.validFileType === $vm.fileTypes.excel) {
          // Converting Excel file into json format
          let reader = new window.FileReader()
          reader.readAsArrayBuffer(file.nativeFile)
          reader.onload = function (e) {
            let data = new Uint8Array(reader.result)
            let arr = []
            for (var i = 0; i !== data.length; ++i) arr[i] = String.fromCharCode(data[i])
            let bstr = arr.join('')
            let wb1 = XLSX.read(bstr, {type: 'binary'})
            jsonArray = XLSX.utils.sheet_to_json(wb1.Sheets[wb1.SheetNames[0]], {raw: true, header: 1})
          }
        }
        $vm.fileName = file.name
        $vm.hasError = false
        let uploadErrors = []
        let acceptFileTypes = $vm.validFileType
        if (file.type.length && !acceptFileTypes.test(file.type)) {
          uploadErrors.push($vm.invalidFileTypeError)
        }
        if (file.size.length && file.size > 10000000) {
          uploadErrors.push('Filesize is too big')
        }
        if (uploadErrors.length > 0) {
          $vm.progress = 0
          $vm.isUploading = false
          $vm.hasError = uploadErrors.join('\n')
        } else {
          file.event('progress', function (sent, total, xhr, event) {
            $vm.progress = parseInt(sent / total * 100, 10)
          })
          file.event('done', function (xhr) {
            $vm.progress = 0
            $vm.isUploading = false
            let json = JSON.parse(xhr.responseText)
            if ($vm.validFileType === $vm.fileTypes.excel) {
              $vm.validate({
                jsonFormat: jsonArray,
                key: json.key,
                fileName: $vm.fileName,
                fileType: json.ext
              })
            } else {
              $vm.validate({
                key: json.key,
                fileName: $vm.fileName,
                fileType: json.ext
              })
            }
          })
          file.event('error', function (event, xhr) {
            $vm.progress = 0
            $vm.isUploading = false
            uploadErrors.push(xhr.responseText)
            $vm.hasError = uploadErrors.join('\n')
          })
          file.sendTo($vm.url, {
            extraHeaders: true,
            method: 'POST'
          })
        }
      })
    })
    zone.event('iframeDone', function (json) {
      $vm.progress = 0
      $vm.isUploading = false
      $vm.validate({
        key: json.key,
        fileName: $vm.fileName,
        fileType: json.ext
      })
    })
    window.fd.addEvent(window.fd.byID('multiple'), 'change', function (e) {
      zone.multiple(e.currentTarget || e.srcElement.checked)
    })
  },
  methods: {
    validate (val = this.value) {
      this.$emit('input', val)
      this.validType = true
      if (val.key) {
        this.isDirty = true
        if (this.options.required) {
          if (val.key !== '') this.isValid = true
          if (this.options.fileTypes && this.options.fileTypes.length) {
            this.isValid = this.validType = this.options.fileTypes.includes(val.fileType)
          }
        } else {
          this.isValid = true
        }
      } else if (this.options.required) {
        this.isDirty = true
        this.isValid = false
      } else {
        this.isValid = true
      }
    }
  }
}

</script>



// WEBPACK FOOTER //
// src/components/inputs/AmazonMediaInput.vue